/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const Complexe = require('../types/Complexe')
const Opef = require('../types/Opef')
const kernel = require('../kernel/kernel')
const CCb = require('./CCb')
const CCbGlob = require('../kernel/CCbGlob')
module.exports = CFonction

/**
 * Classe représentant dans un arbre binaire de calcul un appel de fonction réelle
 * à une variable prédéfinie.
 * @constructor
 * @extends CCb
 * @param {CListeObjets} listeProprietaire : La liste propriétaire de l'objet
 * @param {mtg32.OperateurFontion} Opef : Donne la nature de la fonction.
 * @param {CCb} operande : La calcu donnant l'opérande.
 * @returns {CFonction}
 */
function CFonction (listeProprietaire, Opef, operande) {
  CCb.call(this, listeProprietaire);
  if (arguments.length !== 1) {
    this.Opef = Opef;
    this.operande = operande;
  }
  this.dejaPositionne = false; // ne servira que pour un appel fonction Rand() de génération d'un nombre aléatoire.
  this.valeurDejaPrise = 0; // Comme son nom l'indique pour les appels de random
  this.valeurDejaPriseImaginaire = 0; // Partie imaginaire pour les fonctions complexes pour les appels de random
  this.z1loc = new Complexe();
}
CFonction.prototype = new CCb();
CFonction.prototype.constructor = CFonction;
CFonction.prototype.superClass = "CCb";
CFonction.prototype.className = "CFonction";

CFonction.prototype.nature = function() {
  return CCbGlob.natFonction;
};
CFonction.prototype.numeroVersion = function() {
  return 2;
};
CFonction.prototype.nombreVariables = function() {
  return 1;
};
CFonction.prototype.getClone = function(listeSource, listeCible) {
  var cloneOperande = this.operande.getClone(listeSource, listeCible);
  var ptcalc = new CFonction(listeCible, this.Opef, cloneOperande);
  // Les trois lignes suivantes ajoutées version 4.4 pour que les valeurs rand ne changent pas dans l'annuler-refaire
  ptcalc.dejaPositionne = this.dejaPositionne;
  ptcalc.valeurDejaPrise = this.valeurDejaPrise;
  ptcalc.valeurDejaPriseImaginaire = this.valeurDejaPriseImaginaire;
  return ptcalc;
};
CFonction.prototype.initialisePourDependance = function() {
  CCb.prototype.initialisePourDependance.call(this);
  this.operande.initialisePourDependance();
};
CFonction.prototype.dependDe = function(p) {
  if (this.elementTestePourDependDe === p) return this.dependDeElementTeste;
  return this.memoriseDependance(CCb.prototype.dependDe.call(this,p) || this.operande.dependDe(p));
};
CFonction.prototype.dependDePourBoucle = function(p) {
  if (this.Opef === Opef.Rand)
  {
    // if (p instanceof CVariableBornee) return true;
    if (p.className === "CVariableBornee") return true;
      else return this.operande.dependDePourBoucle(p);
  }
  else return this.operande.dependDePourBoucle(p);
};
CFonction.prototype.existe = function(infoRandom) {
  return this.operande.existe(infoRandom);
};
CFonction.prototype.resultatBase = function(infoRandom, x) {
  switch (this.Opef)
  {
    case Opef.Abso:
      return Math.abs(x);
    case Opef.Enti:
      return Math.floor(x);
    case Opef.Raci:
    case Opef.Raci2:
      if (x >= 0)
        return Math.sqrt(x);
      else throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
    case Opef.Sinu:
      if (this.listeProprietaire.uniteAngle === kernel.uniteAngle.radian) return CCbGlob.sinus(x);
      else return CCbGlob.sinus(x*kernel.ConvDegRad);
    case Opef.Cosi:
      if (this.listeProprietaire.uniteAngle === kernel.uniteAngle.radian) return CCbGlob.cosinus(x);
      else return CCbGlob.cosinus(x*kernel.ConvDegRad);
    case Opef.Tang:
      if (this.listeProprietaire.uniteAngle === kernel.uniteAngle.radian) return CCbGlob.tangente(x);
      else {
        return CCbGlob.tangente(x*kernel.ConvDegRad);
      }
    case Opef.Logn:
      if (x>0) return Math.log(x);
      else throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
    case Opef.Expo:
      return Math.exp(x);
    case Opef.ATan:
      if (this.listeProprietaire.uniteAngle === kernel.uniteAngle.radian) return Math.atan(x);
      else return Math.atan(x)*kernel.ConvRadDeg;
    case Opef.ACos:
      if (this.listeProprietaire.uniteAngle === kernel.uniteAngle.radian) return Math.acos(x);
      else return Math.acos(x)*kernel.ConvRadDeg;
    case Opef.ASin:
      if (this.listeProprietaire.uniteAngle === kernel.uniteAngle.radian) return Math.asin(x);
      else return Math.asin(x)*kernel.ConvRadDeg;
    case Opef.Cosh:
      return (Math.exp(x) + Math.exp(-x))/2;
    case Opef.Sinh:
      return (Math.exp(x) - Math.exp(-x))/2;
    case Opef.Tanh:
      var a = Math.exp(x);
      var b = Math.exp(-x);
      return (a-b)/(a+b);
    case Opef.ArgCh:
      if (x >= 1) return Math.log(x + Math.sqrt(x*x-1));
      else throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
    case Opef.ArgSh:
      return Math.log(x + Math.sqrt(x*x+1));
    case Opef.ArgTh:
      if ((x > -1) && (x < 1 )) return 0.5*Math.log((1+x)/(1-x));
      else throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
    case Opef.Rand:
      if (infoRandom) {
        this.valeurDejaPrise = Math.random();
        this.dejaPositionne = true;
        return this.valeurDejaPrise;
      }
      else {
        if (!this.dejaPositionne)
        {
          this.valeurDejaPrise = Math.random();
          this.dejaPositionne = true;
          return this.valeurDejaPrise;
        }
        else return this.valeurDejaPrise;
      }
    case Opef.fact:
      if ((x < 0) || (x !== Math.round(x))) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      if (x <= 32) {
        var p = 1;
        for (var i = 2; i <= x; i++) {
          p = p*i;
        }
        return p;
      }
      else {
        var x1 = x;
        var d = 1 + 1/(12*x1);
        x1 *= x;
        d += 1/(288*x1);
        x1 *= x;
        d -= 139/(51840*x1);
        x1 *= x;
        d -= 571/(2488320*x1);
        x1*=x;
        d -= 163879/(209018880*x1);
        return Math.exp(0.5*Math.log(2*Math.PI*x)+x*(Math.log(x)-1))*d;
      }
    default : throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  }// switch Opef}
};
CFonction.prototype.resultat = function(infoRandom) {
  switch (this.Opef) {
  case Opef.Left :
  case Opef.LeftC : // Rajouté version 4.7.2
    var mg = this.operande.membreGauche();
    if (mg === null) {
      throw new Erreur(kernel.erreurCalculExceptionkernel._digit64_standard);
    }
    else return this.operande.membreGauche().resultat(infoRandom);
  case Opef.Right :
  case Opef.RightC : // Rajouté version 4.7.2
    var md = this.operande.membreDroit();
    if (md === null) {
      throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
    }
    return this.operande.membreDroit().resultat(infoRandom);
  default :
    var x1 = this.operande.resultat(infoRandom);
    return this.resultatBase(infoRandom, x1);
  }
};
CFonction.prototype.resultatFonction = function(infoRandom, valeurParametre) {
  switch (Opef) {
  case Opef.Left :
  case Opef.LeftC : // Rajouté version 4.7.2
    var mg = this.operande.membreGauche();
    if (mg === null) {
      throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard());
    }
    return this.operande.membreGauche().resultatFonction(infoRandom, valeurParametre);
  case Opef.Right :
  case Opef.RightC : // Rajouté version 4.7.2
    var md = this.operande.membreDroit();
    if (md === null) {
      throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
    }
    return this.operande.membreDroit().resultatFonction(infoRandom, valeurParametre);
  default :
    var x1 = this.operande.resultatFonction(infoRandom, valeurParametre);
    return this.resultatBase(infoRandom, x1);
  }
};

CFonction.prototype.resultatFonction = function(infoRandom, valeurParametre) {
  switch (this.Opef) {
  case Opef.Left :
  case Opef.LeftC : // Rajouté version 4.7.2
    var mg = this.operande.membreGauche();
    if (mg === null) {
      throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
    }
    return this.operande.membreGauche().resultatFonction(infoRandom, valeurParametre);
  case Opef.Right :
  case Opef.RightC : // Rajouté version 4.7.2
    var md = this.operande.membreDroit();
    if (md === null) {
      throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
    }
    return this.operande.membreDroit().resultatFonction(infoRandom, valeurParametre);
  default :
    var x1 = this.operande.resultatFonction(infoRandom, valeurParametre);
    return this.resultatBase(infoRandom, x1);
  }
};
CFonction.prototype.resultatComplexeBase = function(infoRandom, z, zRes) {
  switch (this.Opef)
  {
    case Opef.Abs:
            zRes.y = 0;
            zRes.x = z.module();
            return;
    case Opef.Abso :
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else   {
        zRes.y = 0;
        zRes.x = Math.abs(z.x);
        return;
      }
    case Opef.Sin:
            z.sinComp(this.listeProprietaire.uniteAngle, zRes);
            return;
    case Opef.Sinu :
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else   {
        zRes.y = 0;
        if (this.listeProprietaire.uniteAngle === kernel.uniteAngle.radian) zRes.x = CCbGlob.sinus(z.x);
        else zRes.x =  CCbGlob.sinus(z.x*kernel.ConvDegRad);
        return;
      }
    case Opef.Cos:
            z.cosComp(this.listeProprietaire.uniteAngle, zRes);
            return;
    case Opef.Cosi :
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else   {
        zRes.y = 0;
        if (this.listeProprietaire.uniteAngle === kernel.uniteAngle.radian) zRes.x = CCbGlob.cosinus(z.x);
        else zRes.x =  CCbGlob.cosinus(z.x*kernel.ConvDegRad);
        return;
      }
    case Opef.Tan:
            z.tanComp(this.listeProprietaire.uniteAngle, zRes);
            return;
    case Opef.Tang :
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else   {
        zRes.y = 0;
        if (this.listeProprietaire.uniteAngle === kernel.uniteAngle.radian) zRes.x = CCbGlob.tangente(z.x);
        else zRes.x =  CCb(z.x*kernel.ConvDegRad);
        return;
      }
    case Opef.Log:
            z.lnComp(this.listeProprietaire.uniteAngle, zRes);
            return;
    case Opef.Logn :
      if ((z.y !== 0) || (z.x <= 0)) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else   {
        zRes.y = 0;
        zRes.x =  Math.log(z.x);
        return;
      }
    case Opef.Exp:
            z.expComp(this.listeProprietaire.uniteAngle, zRes);
            return;
    case  Opef.Expo:
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else   {
        zRes.y = 0;
        zRes.x =  Math.exp(z.x);
        return;
      }
    case Opef.Ch:
            z.chComp(this.listeProprietaire.uniteAngle, zRes);
            return;
    case Opef.Cosh:
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else   {
        zRes.y = 0;
        zRes.x = (Math.exp(z.x) + Math.exp(-z.x))/2;
        return;
      }
    case Opef.Sh:
            z.shComp(this.listeProprietaire.uniteAngle,zRes);
            return;
    case Opef.Sinh :
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else   {
        zRes.y = 0;
        zRes.x = (Math.exp(z.x) - Math.exp(-z.x))/2;
        return;
      }
    case Opef.Th:
            z.thComp(this.listeProprietaire.uniteAngle,zRes);
            return;
    case Opef.Tanh :
      if (z.y !== 0) throw new Error(kernel.erreurCalculException);
      else   {
        zRes.y = 0;
        var a = Math.exp(z.x);
        var b = Math.exp(-z.x);
        zRes.x = (a-b)/(a+b);
        return;
      }
    case Opef.Rnd:
      zRes.y = 0;
      if (infoRandom) {
        this.valeurDejaPrise = Math.random();
        this.dejaPositionne = true;
        zRes.x = this.valeurDejaPrise;
      }
      else {
        if (!this.dejaPositionne) {
          this.valeurDejaPrise = Math.random();
          this.dejaPositionne = true;
          zRes.x = this.valeurDejaPrise;
        }
        else zRes.x = this.valeurDejaPrise;
      }
      break;
    case Opef.Conj:
      zRes.x = z.x;
      zRes.y = -z.y;
      return;
    case Opef.Arg:
      if ((z.x === 0) && (z.y === 0))
      {
        zRes.x = 0;
        zRes.y = 0;
        throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      }
      else {
        zRes.x = z.argument(this.listeProprietaire.uniteAngle);
        zRes.y = 0;
        return;
      }
    case Opef.Re:
      zRes.x = z.x;
      zRes.y = 0;
      return;
    case Opef.Im:
      zRes.x = z.y;
      zRes.y = 0;
      return;
    case Opef.Rac:
    case Opef.Rac2:
    case Opef.Raci:
    case Opef.Raci2:
      zRes.x = 0;
      zRes.y = 0;
      if ((z.y !== 0) || (z.x <0)) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else {
        zRes.x = Math.sqrt(z.x);
        return;
      }
    case Opef.EntiC :
    case Opef.Enti :
      zRes.x = 0;
      zRes.y = 0;
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else {
        zRes.x = Math.floor(z.x);
        return;
      }
    case Opef.ACosC :
    case Opef.ACos :
      // le résultat n'existe que si l'argument est réel
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else {
        zRes.y = 0;
        if (this.listeProprietaire.uniteAngle === kernel.uniteAngle.radian) zRes.x = Math.acos(z.x);
        else zRes.x = Math.acos(z.x)*kernel.ConvRadDeg;
        return;
      }
    case Opef.ASinC :
    case Opef.ASin :
      // le résultat n'existe que si l'argument est réel
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else {
        zRes.y = 0;
        if (this.listeProprietaire.uniteAngle === kernel.uniteAngle.radian) zRes.x = Math.asin(z.x);
        else zRes.x = Math.asin(z.x)*kernel.ConvRadDeg;
        return;
      }
    case Opef.ATanC :
    case Opef.ATan :
      // le résultat n'existe que si l'argument est réel
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else {
        zRes.y = 0;
        if (this.listeProprietaire.uniteAngle === kernel.uniteAngle.radian) zRes.x = Math.atan(z.x);
        else zRes.x = Math.atan(z.x)*kernel.ConvRadDeg;
        return;
      }
    case Opef.ArgChC :
    case Opef.ArgCh :
      // le résultat n'existe que si l'argument est réel
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else {
        zRes.y = 0;
        if (z.x >= 1) zRes.x = Math.log(z.x + Math.sqrt(z.x*z.x-1));
        else throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      }
      break;
    case Opef.ArgShC :
    case Opef.ArgSh :
      // le résultat n'existe que si l'argument est réel
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else {
        zRes.y = 0;
        zRes.x = Math.log(z.x + Math.sqrt(z.x*z.x+1));
        return;
      }
    case Opef.ArgThC :
      // le résultat n'existe que si l'argument est réel
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else {
        zRes.y = 0;
        if ((z.x > -1) && (z.x < 1 )) zRes.x = 0.5*Math.log((1+z.x)/(1-z.x));
        else throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      }
      break;
    case Opef.factC:
    case Opef.fact:
      if (z.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else {
        zRes.y = 0;
        if ((z.x < 0) ||(z.x !== Math.round(z.x))) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
          if (z.x <= 32) {
            var p = 1;
            for (var i = 2; i <= z.x; i++) {
              p = p*i;
            }
            zRes.x = p;
          }
          else {
            var x = z.x;
            var x1 = x;
            var d = 1 + 1/(12*x1);
            x1 *= x;
            d += 1/(288*x1);
            x1 *= x;
            d -= 139/(51840*x1);
            x1 *= x;
            d -= 571/(2488320*x1);
            x1*=x;
            d -= 163879/(209018880*x1);
            zRes.x = Math.exp(0.5*Math.log(2*Math.PI*x)+x*(Math.log(x)-1))*d;
          }
      }
  } // switch Opef}
};
CFonction.prototype.resultatComplexe = function(infoRandom, zRes) {
  switch (this.Opef) {
    case Opef.Left :
    case Opef.LeftC : // Rajouté version 4.7.2
      this.operande.membreGauche().resultatComplexe(infoRandom, zRes);
      break; // Rajouté version 4.7.2
    case Opef.Right :
    case Opef.RightC : // Rajouté version 4.7.2
      this.operande.membreDroit().resultatComplexe(infoRandom, zRes);
      break; // Rajouté version 4.7.2
    default :
      this.operande.resultatComplexe(infoRandom, this.z1loc);
      this.resultatComplexeBase(infoRandom, this.z1loc, zRes);
  }
};
CFonction.prototype.resultatFonctionComplexe = function(infoRandom, valeurParametre, zRes){
  switch (this.Opef) {
    case Opef.Left :
    case Opef.LeftC : // Rajouté version 4.7.2
      var mg = this.operande.membreGauche();
      if (mg === null) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      mg.resultatFonctionComplexe(infoRandom, valeurParametre, zRes);
      break; // Rajouté version 4.7.2
    case Opef.Right :
    case Opef.RightC : // Rajouté version 4.7.2
      var md = this.operande.membreDroit();
      if (md === null) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      md.resultatFonctionComplexe(infoRandom, valeurParametre, zRes);
      break; // Rajouté version 4.7.2
    default :
      this.operande.resultatFonctionComplexe(infoRandom, valeurParametre, this.z1loc);
      this.resultatComplexeBase(infoRandom, this.z1loc, zRes);
  }
};
CFonction.prototype.dependDeVariable = function(ind) {
  switch (this.Opef) {
    case Opef.Left:
    case Opef.LeftC:
      return this.operande.membreGauche().dependDeVariable(ind);
    case Opef.Right:
    case Opef.RightC:
      return this.operande.membreDroit().dependDeVariable(ind);
    default:
      return this.operande.dependDeVariable(ind);
  }
};
CFonction.prototype.getCopie = function() {
  return new CFonction(this.listeProprietaire, this.Opef, this.operande.getCopie());
};
CFonction.prototype.chaineCalcul = function(varFor) {
  var cha = this.operande.chaineCalculSansPar(varFor);
  var parouv = "(";
  var parfer = ")";

  switch (this.Opef)
  {
    case Opef.Abso:
    case Opef.Abs:
      return kernel.getString("abs") + parouv + cha + parfer;
    case Opef.Enti:
    case Opef.EntiC:
      return kernel.getString("intgr") + parouv + cha + parfer;
    case Opef.Raci:
    case Opef.Rac:
      return kernel.getString("sqrt") + parouv + cha + parfer;
    case Opef.Raci2:
    case Opef.Rac2:
      return kernel.getString("rac") + parouv + cha + parfer;
    case Opef.Sinu:
    case Opef.Sin:
      return kernel.getString("sin") + parouv + cha + parfer;
    case Opef.Cosi:
    case Opef.Cos:
      return kernel.getString("cos") + parouv + cha + parfer;
    case Opef.Tang:
    case Opef.Tan:
      return kernel.getString("tan") + parouv + cha + parfer;
    case Opef.Logn:
    case Opef.Log:
      return kernel.getString("ln") + parouv + cha + parfer;
    case Opef.Expo:
    case Opef.Exp:
      return kernel.getString("exp") + parouv + cha + parfer;
    case Opef.ATan:
    case Opef.ATanC:
      return kernel.getString("arctan") + parouv + cha + parfer;
    case Opef.ACos:
    case Opef.ACosC:
      return kernel.getString("arccos") + parouv + cha + parfer;
    case Opef.ASin:
    case Opef.ASinC:
      return kernel.getString("arcsin") + parouv + cha + parfer;
    case Opef.Cosh:
    case Opef.Ch:
      return kernel.getString("ch") + parouv + cha + parfer;
    case Opef.Sinh:
    case Opef.Sh:
      return kernel.getString("sh") + parouv + cha + parfer;
    case Opef.Tanh:
    case Opef.Th:
      return kernel.getString("th") + parouv + cha + parfer;
    case Opef.fact:
    case Opef.factC:
      return kernel.getString("fact") + parouv + cha + parfer;
    // Deux ajouts version 4.6
    case Opef.Left:
    case Opef.LeftC:
        return kernel.getString("left") + parouv + cha + parfer;
    case Opef.Right:
    case Opef.RightC:
        return kernel.getString("right") + parouv + cha + parfer;
    // A revoir ici. Pour la version 1.5 j'ai oublie le ArgCh
    case Opef.ArgCh:
    case Opef.ArgChC:
      return kernel.getString("argch") + parouv + cha + parfer;
    case Opef.ArgSh:
    case Opef.ArgShC:
      return kernel.getString("argsh") + parouv + cha + parfer;
    case Opef.ArgTh:
    case Opef.ArgThC:
      return kernel.getString("argth") + parouv + cha + parfer;
    case Opef.Rand:
    case Opef.Rnd:
      return kernel.getString("rand") + parouv + cha + parfer;
    case Opef.Conj:
      return kernel.getString("conj") + parouv + cha + parfer;
    case Opef.Arg:
      return kernel.getString("arg") + parouv + cha + parfer;
    case Opef.Re:
      return kernel.getString("re") + parouv + cha + parfer;
    case Opef.Im:
      return kernel.getString("im") + parouv + cha + parfer;

    default:
      return "";
  } // switch Opef}}
};
CFonction.prototype.chaineLatex = function(varFor) {
var parouvLatex = "\\left(";
var parferLatex = "\\right)";
var cha = this.operande.chaineLatexSansPar(varFor);

  switch (this.Opef)
  {
    case Opef.Abso:
    case Opef.Abs:
      return "\\left| {"+ cha + "} \\right|";
    case Opef.Enti:
    case Opef.EntiC:
      return kernel.getString("intgr") + parouvLatex + cha + parferLatex;
    case Opef.Raci:
    case Opef.Rac:
      return "\\sqrt{" + cha + "}";
    case Opef.Raci2:
    case Opef.Rac2:
      return "\\sqrt{" + cha + "}";
    case Opef.Sinu:
    case Opef.Sin:
      return kernel.getString("sin") + parouvLatex + cha + parferLatex;
    case Opef.Cosi:
    case Opef.Cos:
      return kernel.getString("cos") + parouvLatex + cha + parferLatex;
    case Opef.Tang:
    case Opef.Tan:
      return kernel.getString("tan") + parouvLatex + cha + parferLatex;
    case Opef.Logn:
    case Opef.Log:
      return kernel.getString("ln") + parouvLatex + cha + parferLatex;
    case Opef.Expo:
    case Opef.Exp:
      return "e^" + "{" + cha + "}";
    case Opef.ATan:
    case Opef.ATanC:
      return kernel.getString("arctan") + parouvLatex + cha + parferLatex;
    case Opef.ACos:
    case Opef.ACosC:
      return kernel.getString("arccos") + parouvLatex + cha + parferLatex;
    case Opef.ASin:
    case Opef.ASinC:
      return kernel.getString("arcsin") + parouvLatex + cha + parferLatex;
    case Opef.Cosh:
    case Opef.Ch:
      return kernel.getString("ch") + parouvLatex + cha + parferLatex;
    case Opef.Sinh:
    case Opef.Sh:
      return kernel.getString("sh") + parouvLatex + cha + parferLatex;
    case Opef.Tanh:
    case Opef.Th:
      return kernel.getString("th") + parouvLatex + cha + parferLatex;
    case Opef.fact:
    case Opef.factC:
      if ((this.operande.nature() & (CCbGlob.natIntegraleDansFormule | CCbGlob.natMoinsUnaire | CCbGlob.natOperation |
        CCbGlob.natSommeDansFormule)) !== 0)
      return parouvLatex + cha + parferLatex + "!";
      else return cha + "!"; // A revoir
    // Deux ajouts version 4.6
    case Opef.Left:
    case Opef.LeftC:
        return kernel.getString("Left") + parouvLatex + cha + parferLatex;
    case Opef.Right:
    case Opef.RightC:
        return kernel.getString("Left") + parouvLatex + cha + parferLatex;
    // A revoir ici. Pour la version 1.5 j'ai oublie le ArgCh
    case Opef.ArgCh:
    case Opef.ArgChC:
      return kernel.getString("argch") + parouvLatex + cha + parferLatex;
    case Opef.ArgSh:
    case Opef.ArgShC:
      return kernel.getString("argsh") + parouvLatex + cha + parferLatex;
    case Opef.ArgTh:
    case Opef.ArgThC:
      return kernel.getString("argth") + parouvLatex + cha + parferLatex;
    case Opef.Rand:
    case Opef.Rnd:
      return kernel.getString("rand") + parouvLatex + cha + parferLatex;
    case Opef.Conj:
      return "\\overline {" + cha + "}";
    case Opef.Arg:
      return kernel.getString("arg") + parouvLatex + cha + parferLatex;
    case Opef.Re:
      return kernel.getString("re") + parouvLatex + cha + parferLatex;
    case Opef.Im:
      return kernel.getString("im") + parouvLatex + cha + parferLatex;

    default:
      return "";
  } // switch Opef}}
};
CFonction.prototype.read = function(inps, list) {
  CCb.prototype.read.call(this, inps, list);
  this. Opef = inps.readByte();
  // A partir de la version 3.0, décalage des indices de fonctions complexes
  if ((this.nVersion === 1) && (this.Opef >= 19)) this.Opef += 45;
  this.operande = inps.readObject(list);
  if (this.Opef === Opef.Rand)
  {
    this.valeurDejaPrise= inps.readDouble();
    this.dejaPositionne = true;
  }
};
CFonction.prototype.write = function(oups, list) {
  CCb.prototype.write.call(this,oups, list);
  oups.writeByte(this.Opef);
  oups.writeObject(this.operande);
  if (this.Opef === Opef.Rand)
  {
    oups.writeDouble(this.valeurDejaPrise);
  }
};
CFonction.prototype.estConstant = function() {
  return (this.Opef !== Opef.Rand) && this.operande.estConstant();
};
CFonction.prototype.deriveePossible = function(indiceVariable) {
  return this.operande.deriveePossible(indiceVariable);
};
CFonction.prototype.calculAvecValeursRemplacees = function() {
  return new CFonction(this.listeProprietaire, this.Opef, this.operande.calculAvecValeursRemplacees());
};




// WEBPACK FOOTER //
// ./src/objets/CFonction.js